<template>
  <div class="collections-container">
    <div class="collections-header">
      <h2>Koleksi Buku Saya</h2>
      <button @click="showCreateModal = true" class="create-btn">
        <font-awesome-icon :icon="['fas', 'plus']" /> Buat Koleksi Baru
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
      <p>Memuat koleksi...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="collections.length === 0" class="empty-state">
      <font-awesome-icon :icon="['fas', 'bookmark']" class="empty-icon" />
      <h3>Belum Ada Koleksi</h3>
      <p>Buatlah koleksi baru untuk menyimpan buku-buku favorit Anda.</p>
      <button @click="showCreateModal = true" class="btn btn-primary">
        <font-awesome-icon :icon="['fas', 'plus']" /> Buat Koleksi Baru
      </button>
    </div>

    <!-- Collections List -->
    <div v-else class="collections-list">
      <!-- Loop through collections -->
      <div v-for="collection in collections" :key="collection.id" class="collection-card">
        <div class="card-header">
          <h3 @click="viewCollection(collection)">{{ collection.name }}</h3>
          <div class="card-actions">
            <button @click="editCollection(collection)" class="action-btn edit-btn">
              <font-awesome-icon :icon="['fas', 'edit']" />
            </button>
            <button @click="confirmDeleteCollection(collection)" class="action-btn delete-btn">
              <font-awesome-icon :icon="['fas', 'trash']" />
            </button>
          </div>
        </div>
        
        <p v-if="collection.description" class="collection-description">{{ collection.description }}</p>
        
        <div class="collection-meta">
          <span class="book-count">
            <font-awesome-icon :icon="['fas', 'book']" /> {{ collection.bookmarks_count }} Buku
          </span>
          <span class="date">
            <font-awesome-icon :icon="['fas', 'calendar']" /> {{ formatDate(collection.updated_at) }}
          </span>
        </div>
        
        <button @click="viewCollection(collection)" class="view-btn">
          Lihat Koleksi <font-awesome-icon :icon="['fas', 'angle-right']" />
        </button>
      </div>
    </div>

    <!-- Create Collection Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal-content">
        <h3>Buat Koleksi Baru</h3>
        <form @submit.prevent="createCollection">
          <div class="form-group">
            <label for="collectionName">Nama Koleksi</label>
            <input
              type="text"
              id="collectionName"
              v-model="newCollection.name"
              placeholder="Contoh: Buku Favorit, Ingin Dibaca, dll"
              required
            />
          </div>
          <div class="form-group">
            <label for="collectionDesc">Deskripsi (Opsional)</label>
            <textarea
              id="collectionDesc"
              v-model="newCollection.description"
              placeholder="Deskripsi singkat tentang koleksi ini"
              rows="3"
            ></textarea>
          </div>
          <div class="form-group checkbox-group">
            <input type="checkbox" id="isPublic" v-model="newCollection.is_public" />
            <label for="isPublic">Publik (Dapat dilihat oleh pengguna lain)</label>
          </div>
          <div class="modal-actions">
            <button type="button" @click="showCreateModal = false" class="btn btn-secondary">Batal</button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              <font-awesome-icon v-if="isSubmitting" :icon="['fas', 'spinner']" spin />
              {{ isSubmitting ? 'Menyimpan...' : 'Simpan' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit Collection Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="showEditModal = false">
      <div class="modal-content">
        <h3>Edit Koleksi</h3>
        <form @submit.prevent="updateCollection">
          <div class="form-group">
            <label for="editCollectionName">Nama Koleksi</label>
            <input
              type="text"
              id="editCollectionName"
              v-model="editedCollection.name"
              placeholder="Nama koleksi"
              required
            />
          </div>
          <div class="form-group">
            <label for="editCollectionDesc">Deskripsi (Opsional)</label>
            <textarea
              id="editCollectionDesc"
              v-model="editedCollection.description"
              placeholder="Deskripsi singkat tentang koleksi ini"
              rows="3"
            ></textarea>
          </div>
          <div class="form-group checkbox-group">
            <input type="checkbox" id="editIsPublic" v-model="editedCollection.is_public" />
            <label for="editIsPublic">Publik (Dapat dilihat oleh pengguna lain)</label>
          </div>
          <div class="modal-actions">
            <button type="button" @click="showEditModal = false" class="btn btn-secondary">Batal</button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              <font-awesome-icon v-if="isSubmitting" :icon="['fas', 'spinner']" spin />
              {{ isSubmitting ? 'Memperbarui...' : 'Perbarui' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- View Collection Modal -->
    <div v-if="showViewModal" class="modal-overlay" @click.self="showViewModal = false">
      <div class="modal-content collection-detail-modal">
        <div class="modal-header">
          <h3>{{ currentCollection.name }}</h3>
          <button @click="showViewModal = false" class="close-btn">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
        
        <p v-if="currentCollection.description" class="collection-description">
          {{ currentCollection.description }}
        </p>
        
        <!-- Books in collection -->
        <div v-if="loadingBooks" class="loading-books">
          <font-awesome-icon :icon="['fas', 'spinner']" spin />
          <p>Memuat buku...</p>
        </div>
        
        <div v-else-if="!currentCollection.bookmarks || currentCollection.bookmarks.length === 0" class="empty-books">
          <font-awesome-icon :icon="['fas', 'book-open']" class="empty-icon" />
          <p>Belum ada buku dalam koleksi ini</p>
          <p class="help-text">Anda dapat menambahkan buku saat menjelajahi buku di HobiBaca</p>
        </div>
        
        <div v-else class="books-grid">
          <div v-for="bookmark in currentCollection.bookmarks" :key="bookmark.id" class="book-card">
            <div class="book-card-inner">
              <div class="book-cover">
                <img :src="bookmark.book_details.cover || defaultCover" :alt="bookmark.book_details.title" />
              </div>
              <div class="book-info">
                <h4>{{ bookmark.book_details.title }}</h4>
                <p class="book-author">{{ bookmark.book_details.author.name }}</p>
                <div class="book-rating">
                  <span v-for="i in 5" :key="i" class="star" 
                    :class="{'filled': i <= Math.round(bookmark.book_details.average_rating)}">
                    <font-awesome-icon :icon="['fas', 'star']" />
                  </span>
                  <span class="rating-value">{{ bookmark.book_details.average_rating }}</span>
                </div>
                <p v-if="bookmark.notes" class="book-notes">
                  <font-awesome-icon :icon="['fas', 'sticky-note']" /> 
                  {{ bookmark.notes }}
                </p>
              </div>
              <div class="book-actions">
                <button @click="goToBook(bookmark.book_details.slug)" class="btn-view">
                  <font-awesome-icon :icon="['fas', 'eye']" /> Lihat
                </button>
                <button @click="editBookmarkNotes(bookmark)" class="btn-edit">
                  <font-awesome-icon :icon="['fas', 'edit']" />
                </button>
                <button @click="removeBookmark(bookmark)" class="btn-remove">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Notes Modal -->
    <div v-if="showNotesModal" class="modal-overlay" @click.self="showNotesModal = false">
      <div class="modal-content">
        <h3>Edit Catatan</h3>
        <p class="book-title">{{ currentBookmark.book_details.title }}</p>
        <form @submit.prevent="updateBookmarkNotes">
          <div class="form-group">
            <label for="bookmarkNotes">Catatan Anda tentang buku ini</label>
            <textarea
              id="bookmarkNotes"
              v-model="currentBookmark.notes"
              placeholder="Contoh: Baru dibaca sampai halaman 50, buku tentang sejarah Indonesia, dll."
              rows="4"
            ></textarea>
          </div>
          <div class="modal-actions">
            <button type="button" @click="showNotesModal = false" class="btn btn-secondary">Batal</button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              <font-awesome-icon v-if="isSubmitting" :icon="['fas', 'spinner']" spin />
              {{ isSubmitting ? 'Menyimpan...' : 'Simpan Catatan' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { useHead } from '@unhead/vue';

export default {
  name: 'Collections',
  setup() {
    useHead({
      title: 'Koleksi Buku | HobiBaca',
      meta: [
        { name: 'description', content: 'Kelola koleksi dan bookmark buku favorit Anda di HobiBaca' },
        { name: 'keywords', content: 'koleksi buku, bookmark, simpan buku, HobiBaca' }
      ]
    });
  },
  data() {
    return {
      collections: [],
      loading: true,
      loadingBooks: false,
      isSubmitting: false,
      showCreateModal: false,
      showEditModal: false,
      showViewModal: false,
      showNotesModal: false,
      
      newCollection: {
        name: '',
        description: '',
        is_public: false
      },
      
      editedCollection: {
        id: null,
        name: '',
        description: '',
        is_public: false
      },
      
      currentCollection: {
        id: null,
        name: '',
        description: '',
        bookmarks: []
      },
      
      currentBookmark: {
        id: null,
        notes: '',
        book_details: { title: '' }
      },
      
      defaultCover: 'https://via.placeholder.com/150x200?text=No+Cover'
    };
  },
  async created() {
    await this.fetchCollections();
    
    // Check if we need to open a specific collection (if navigated with ID parameter)
    const collectionId = this.$route.query.id;
    if (collectionId) {
      const collection = this.collections.find(c => c.id == collectionId);
      if (collection) {
        this.viewCollection(collection);
      }
    }
  },
  methods: {
    async fetchCollections() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/collections/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        this.collections = response.data;
        
        // Create default collection if no collections exist
        if (this.collections.length === 0) {
          await this.createDefaultCollection();
        }
      } catch (error) {
        console.error('Error fetching collections:', error);
        this.handleApiError(error, 'Gagal memuat koleksi');
      } finally {
        this.loading = false;
      }
    },

    async createDefaultCollection() {
      try {
        const token = localStorage.getItem('token');
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/collections/default/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Add the default collection to the collections array
        this.collections = [response.data];
      } catch (error) {
        console.error('Error creating default collection:', error);
      }
    },

    async createCollection() {
      if (!this.newCollection.name.trim()) {
        this.showToast('error', 'Nama koleksi tidak boleh kosong');
        return;
      }

      this.isSubmitting = true;
      try {
        const token = localStorage.getItem('token');
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/collections/`,
          this.newCollection,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Add the new collection to the collections array
        this.collections.push(response.data);
        
        // Reset form and close modal
        this.newCollection = {
          name: '',
          description: '',
          is_public: false
        };
        this.showCreateModal = false;
        
        this.showToast('success', 'Koleksi berhasil dibuat');
      } catch (error) {
        console.error('Error creating collection:', error);
        
        if (error.response?.data?.name) {
          this.showToast('error', `Error: ${error.response.data.name[0]}`);
        } else {
          this.handleApiError(error, 'Gagal membuat koleksi');
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    editCollection(collection) {
      this.editedCollection = {
        id: collection.id,
        name: collection.name,
        description: collection.description || '',
        is_public: collection.is_public
      };
      this.showEditModal = true;
    },

    async updateCollection() {
      if (!this.editedCollection.name.trim()) {
        this.showToast('error', 'Nama koleksi tidak boleh kosong');
        return;
      }

      this.isSubmitting = true;
      try {
        const token = localStorage.getItem('token');
        
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/api/collections/${this.editedCollection.id}/`,
          {
            name: this.editedCollection.name,
            description: this.editedCollection.description,
            is_public: this.editedCollection.is_public
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Update collection in the collections array
        const index = this.collections.findIndex(c => c.id === this.editedCollection.id);
        if (index !== -1) {
          this.collections[index] = response.data;
        }
        
        // Also update current collection if it's the same one
        if (this.currentCollection.id === this.editedCollection.id) {
          this.currentCollection.name = response.data.name;
          this.currentCollection.description = response.data.description;
          this.currentCollection.is_public = response.data.is_public;
        }
        
        // Close modal
        this.showEditModal = false;
        
        this.showToast('success', 'Koleksi berhasil diperbarui');
      } catch (error) {
        console.error('Error updating collection:', error);
        this.handleApiError(error, 'Gagal memperbarui koleksi');
      } finally {
        this.isSubmitting = false;
      }
    },

    confirmDeleteCollection(collection) {
      Swal.fire({
        title: 'Hapus Koleksi?',
        text: `Anda yakin ingin menghapus koleksi "${collection.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ya, Hapus',
        cancelButtonText: 'Batal'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteCollection(collection.id);
        }
      });
    },

    async deleteCollection(collectionId) {
      try {
        const token = localStorage.getItem('token');
        
        await axios.delete(
          `${process.env.VUE_APP_API_BASE_URL}/api/collections/${collectionId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Remove collection from the collections array
        this.collections = this.collections.filter(c => c.id !== collectionId);
        
        // Close view modal if it's the same collection
        if (this.currentCollection.id === collectionId) {
          this.showViewModal = false;
        }
        
        this.showToast('success', 'Koleksi berhasil dihapus');
      } catch (error) {
        console.error('Error deleting collection:', error);
        this.handleApiError(error, 'Gagal menghapus koleksi');
      }
    },

    async viewCollection(collection) {
      this.loadingBooks = true;
      this.currentCollection = { ...collection, bookmarks: [] };
      this.showViewModal = true;
      
      try {
        const token = localStorage.getItem('token');
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/collections/${collection.id}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        this.currentCollection = response.data;
      } catch (error) {
        console.error('Error fetching collection details:', error);
        this.handleApiError(error, 'Gagal memuat detail koleksi');
      } finally {
        this.loadingBooks = false;
      }
    },

    goToBook(slug) {
      this.$router.push(`/book/${slug}`);
    },

    editBookmarkNotes(bookmark) {
      this.currentBookmark = { ...bookmark };
      this.showNotesModal = true;
    },

    async updateBookmarkNotes() {
      this.isSubmitting = true;
      try {
        const token = localStorage.getItem('token');
        
        const response = await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/api/bookmarks/${this.currentBookmark.id}/`,
          {
            notes: this.currentBookmark.notes
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Update bookmark in the current collection
        const index = this.currentCollection.bookmarks.findIndex(b => b.id === this.currentBookmark.id);
        if (index !== -1) {
          this.currentCollection.bookmarks[index].notes = response.data.notes;
        }
        
        // Close modal
        this.showNotesModal = false;
        
        this.showToast('success', 'Catatan berhasil diperbarui');
      } catch (error) {
        console.error('Error updating bookmark notes:', error);
        this.handleApiError(error, 'Gagal memperbarui catatan');
      } finally {
        this.isSubmitting = false;
      }
    },

    async removeBookmark(bookmark) {
      Swal.fire({
        title: 'Hapus Bookmark?',
        text: `Anda yakin ingin menghapus buku "${bookmark.book_details.title}" dari koleksi ini?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ya, Hapus',
        cancelButtonText: 'Batal'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteBookmark(bookmark);
        }
      });
    },

    async deleteBookmark(bookmark) {
      try {
        const token = localStorage.getItem('token');
        
        await axios.delete(
          `${process.env.VUE_APP_API_BASE_URL}/api/collections/${this.currentCollection.id}/remove-book/${bookmark.id}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Remove bookmark from the current collection
        this.currentCollection.bookmarks = this.currentCollection.bookmarks.filter(b => b.id !== bookmark.id);
        
        // Update bookmarks count in collections list
        const index = this.collections.findIndex(c => c.id === this.currentCollection.id);
        if (index !== -1) {
          this.collections[index].bookmarks_count--;
        }
        
        this.showToast('success', 'Buku berhasil dihapus dari koleksi');
      } catch (error) {
        console.error('Error deleting bookmark:', error);
        this.handleApiError(error, 'Gagal menghapus buku dari koleksi');
      }
    },

    handleApiError(error, defaultMessage) {
      if (error.response) {
        if (error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }
        
        const errorMessage = 
          error.response.data?.detail || 
          error.response.data?.error || 
          error.response.data?.message || 
          defaultMessage;
        
        this.showToast('error', errorMessage);
      } else {
        this.showToast('error', defaultMessage);
      }
    },

    showToast(icon, text) {
      Swal.fire({
        icon,
        text,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>
<style scoped>
.collections-container {
  max-width: 1200px;
  margin: 40px auto;
  padding: 20px;
}

.collections-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.collections-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0;
}

.create-btn {
  background-color: #488e71;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s;
}

.create-btn:hover {
  background-color: #3a7259;
}

/* Loading and empty states */
.loading-state,
.empty-state {
  text-align: center;
  margin: 50px 0;
  padding: 30px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.loading-icon,
.empty-icon {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 20px;
}

.empty-state h3 {
  margin-bottom: 10px;
  color: #555;
}

.empty-state p {
  color: #777;
  margin-bottom: 20px;
}

/* Collections grid */
.collections-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.collection-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
}

.collection-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.card-header h3 {
  font-size: 18px;
  margin: 0;
  cursor: pointer;
  color: #488e71;
}

.card-header h3:hover {
  text-decoration: underline;
}

.card-actions {
  display: flex;
  gap: 5px;
}

.action-btn {
  background: none;
  border: none;
  font-size: 14px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #777;
}

.edit-btn:hover {
  color: #488e71;
  background-color: rgba(72, 142, 113, 0.1);
}

.delete-btn:hover {
  color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.collection-description {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #666;
  flex-grow: 1;
}

.collection-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 14px;
  color: #888;
}

.book-count,
.date {
  display: flex;
  align-items: center;
  gap: 5px;
}

.view-btn {
  background-color: transparent;
  border: 1px solid #488e71;
  color: #488e71;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: background-color 0.3s, color 0.3s;
}

.view-btn:hover {
  background-color: #488e71;
  color: white;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 25px;
  width: 100%;
  max-width: 500px;
  position: relative;
}

.collection-detail-modal {
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.modal-header h3 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #777;
}

.close-btn:hover {
  color: #333;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #555;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkbox-group input {
  width: auto;
}

.checkbox-group label {
  margin-bottom: 0;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn {
  padding: 10px 15px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.btn-primary {
  background-color: #488e71;
  color: white;
}

.btn-primary:hover {
  background-color: #3a7259;
}

.btn-primary:disabled {
  background-color: #93c3b1;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #333;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #e9ecef;
}

/* Collection detail view */
.loading-books,
.empty-books {
  text-align: center;
  padding: 30px;
  color: #777;
}

.empty-books .empty-icon {
  margin-bottom: 15px;
}

.help-text {
  font-size: 14px;
  color: #999;
  font-style: italic;
}

.books-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.book-card {
  border-radius: 8px;
  overflow: hidden;
  background-color: #f8f9fa;
  transition: transform 0.3s;
}

.book-card:hover {
  transform: translateY(-5px);
}

.book-card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.book-cover {
  height: 200px;
  overflow: hidden;
}

.book-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.book-info {
  padding: 15px;
  flex-grow: 1;
}

.book-info h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
  margin-bottom: 5px;
}

.book-author {
  color: #666;
  font-size: 14px;
  margin-bottom: 10px;
}

.book-rating {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
}

.star {
  color: #ddd;
}

.star.filled {
  color: #ffc107;
}

.rating-value {
  font-size: 14px;
  color: #666;
}

.book-notes {
  font-size: 14px;
  color: #666;
  margin-top: 10px;
  display: flex;
  align-items: flex-start;
  gap: 5px;
}

.book-actions {
  display: flex;
  border-top: 1px solid #e9ecef;
}

.book-actions button {
  flex: 1;
  padding: 10px;
  border: none;
  background: none;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-view {
  color: #488e71;
}

.btn-edit {
  color: #6c757d;
}

.btn-remove {
  color: #dc3545;
}

.btn-view:hover {
  background-color: rgba(72, 142, 113, 0.1);
}

.btn-edit:hover {
  background-color: rgba(108, 117, 125, 0.1);
}

.btn-remove:hover {
  background-color: rgba(220, 53, 69, 0.1);
}

.book-title {
  color: #488e71;
  font-weight: 600;
  margin-bottom: 15px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .collections-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .collections-list {
    grid-template-columns: 1fr;
  }
  
  .books-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-content {
    width: 90%;
    padding: 15px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .modal-actions button {
    width: 100%;
  }
}
</style>